import React from 'react';
import { Gift, Search, FileText, Sparkles } from 'lucide-react';

export default function RefundsTab({
  searchTerm,
  setSearchTerm,
  sidebarStatusFilter,
  setSidebarStatusFilter,
  sidebarPropFilter,
  setSidebarPropFilter,
  sidebarRoomFilter,
  setSidebarRoomFilter,
  PROPRIEDADES,
  stays,
  receipts,
  formatDateToPt,
  setActiveTab,
  focarInquilinoNaTimeline,
  setQuickReceiptData
}) {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <Gift className="text-amber-500 h-6 w-6" /> Centro de Devolução de Cauções
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            Lista de inquilinos com contratos finalizados ou em processo de check-out para acerto de contas.
          </p>
        </div>
      </div>

      {/* BARRA DE FILTROS */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Pesquisar inquilino..."
            value={searchTerm || ""}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:bg-white transition-all text-slate-800"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
          <select
            value={sidebarStatusFilter}
            onChange={(e) => setSidebarStatusFilter(e.target.value)}
            className="bg-amber-50 border border-amber-200 text-xs font-black px-3 py-2 rounded-xl focus:outline-none text-amber-800"
          >
            <option value="Todos">Todos os Estados</option>
            <option value="Em Processo">⏳ Em Processo</option>
            <option value="Devolvida">✅ Devolvida</option>
          </select>

          <select
            value={sidebarPropFilter}
            onChange={(e) => setSidebarPropFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 text-xs font-bold px-3 py-2 rounded-xl focus:outline-none text-slate-800"
          >
            <option value="Todos">Todos os Apartamentos</option>
            {PROPRIEDADES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Quarto (ex: Quarto 1)"
            value={sidebarRoomFilter || ""}
            onChange={(e) => setSidebarRoomFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 text-xs font-bold px-3 py-2 rounded-xl focus:outline-none text-slate-800 w-36"
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] font-black border-b border-slate-200">
                <th className="px-6 py-4">Inquilino / Casa</th>
                <th className="px-6 py-4">Início Contrato</th>
                <th className="px-6 py-4">Fim Contrato</th>
                <th className="px-6 py-4 text-center text-rose-600">Prazo Reembolso (2M)</th>
                <th className="px-6 py-4 text-right">Caução Inicial</th>
                <th className="px-6 py-4 text-center">Descontos (Manuais)</th>
                <th className="px-6 py-4 text-right">Valor Líquido</th>
                <th className="px-6 py-4">Fatura Sinal / NC</th>
                <th className="px-6 py-4 text-center">Estado</th>
                <th className="px-6 py-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {stays
                .filter((stay) => {
                  const hoje = new Date();
                  const dataFim = new Date(stay.checkOut);
                  const statusInquilino = stay.statusCaucao || "Em Processo";

                  const matchStatus = sidebarStatusFilter === "Todos" || statusInquilino === sidebarStatusFilter;
                  const condicaoBase = dataFim <= hoje || statusInquilino === "Em Processo" || statusInquilino === "Devolvida";
                  const matchSearch = stay.inquilino.toLowerCase().includes((searchTerm || "").toLowerCase());
                  const matchProp = sidebarPropFilter === "Todos" || stay.propriedade === sidebarPropFilter;
                  const matchRoom = !sidebarRoomFilter || stay.quarto.toLowerCase().includes(sidebarRoomFilter.toLowerCase());

                  return condicaoBase && matchSearch && matchProp && matchRoom && matchStatus;
                })
                .sort((a, b) => new Date(a.checkOut).getTime() - new Date(b.checkOut).getTime())
                .map((stay) => {
                  const trancheCaucao = receipts.find(
                    (r) => r.stayId === stay.id && r.id.includes("tranche-caucao")
                  );
                  const refFaturaOriginal = trancheCaucao?.refFatura || "Sem Fatura";

                  const reducoesExtra = parseFloat(stay.reducoesExtra) || 0;
                  const despesasExtra = parseFloat(stay.despesasExtra) || 0;
                  const taxaLimpeza = parseFloat(stay.taxaLimpeza) || 0;
                  const caucaoInicial = parseFloat(stay.caucao) || 0;

                  const valorLiquido = Math.max(
                    0,
                    caucaoInicial - (reducoesExtra + despesasExtra + taxaLimpeza)
                  );

                  const dataTermino = new Date(stay.checkOut);
                  const dataLimite = new Date(dataTermino.setMonth(dataTermino.getMonth() + 2));
                  const dataLimiteStr = dataLimite.toISOString().slice(0, 10);

                  const hojeRef = new Date();
                  const prazoUltrapassado = dataLimite < hojeRef && stay.statusCaucao !== "Devolvida";
                  const corPrazo = prazoUltrapassado
                    ? "bg-red-50 text-red-700 border-red-100"
                    : "bg-emerald-50 text-emerald-700 border-emerald-100";

                  return (
                    <tr key={stay.id} className="hover:bg-slate-50/40 transition-colors">
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveTab("recebimentos");
                            if (typeof focarInquilinoNaTimeline === "function") {
                              focarInquilinoNaTimeline(stay.id);
                            }
                          }}
                          className="text-left block group focus:outline-none"
                        >
                          <span className="font-black text-slate-900 group-hover:text-indigo-600 group-hover:underline transition-all block">
                            {stay.inquilino}
                          </span>
                        </button>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {stay.propriedade} • {stay.quarto}
                        </p>
                        {stay.estadiasAnteriores && (
                          <p className="text-[10px] text-amber-700 bg-amber-50/70 px-2 py-1 rounded-lg mt-1.5 border border-amber-100 italic block max-w-[180px] whitespace-pre-wrap break-words">
                            xml ⏮️ {stay.estadiasAnteriores}
                          </p>
                        )}
                      </td>

                      <td className="px-6 py-4 text-slate-500 font-medium">
                        {formatDateToPt(stay.checkIn)}
                      </td>
                      <td className="px-6 py-4 text-slate-900 font-bold">
                        {formatDateToPt(stay.checkOut)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`${corPrazo} font-bold px-2.5 py-1 rounded-xl block w-fit mx-auto font-mono`}>
                          {formatDateToPt(dataLimiteStr)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right data-mono text-slate-900">
                        {caucaoInicial.toFixed(2)}€
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 text-[10px] max-w-[180px] mx-auto bg-slate-50 p-2 rounded-xl border">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Reduções Extra:</span>
                            <span className="font-bold text-rose-600">{reducoesExtra.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Despesas Extra:</span>
                            <span className="font-bold text-rose-600">{despesasExtra.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Taxa Limpeza Saída:</span>
                            <span className="font-bold text-rose-600">{taxaLimpeza.toFixed(2)}€</span>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-right font-black text-emerald-600 text-sm">
                        {valorLiquido.toFixed(2)}€
                      </td>

                      <td className="px-6 py-4 space-y-1">
                        <div className="flex items-center gap-1 text-[10px] text-slate-500">
                          <FileText size={11} className="text-indigo-500" />
                          <span>FT: {refFaturaOriginal}</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-slate-700">
                          <Sparkles size={11} className="text-amber-500" />
                          <span className="font-mono bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                            NC: {stay.refNotaCredito || "Pendente"}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-2.5 py-1 rounded-xl text-[9px] font-black uppercase border ${
                          stay.statusCaucao === "Devolvida"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>
                          {stay.statusCaucao || "Em Processo"}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setQuickReceiptData({
                              ...stay,
                              isCaucaoAjuste: true,
                              reducoesExtra: reducoesExtra,
                              despesasExtra: despesasExtra,
                              taxaLimpeza: taxaLimpeza,
                              refNotaCredito: stay.refNotaCredito || "",
                              statusCaucao: stay.statusCaucao || "Em Processo",
                            });
                          }}
                          className="bg-slate-900 text-white font-bold px-3 py-1.5 rounded-xl text-[10px] uppercase hover:bg-slate-800 transition-all"
                        >
                          Ajustar / Liquidar
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
